import { isObject } from '@/helpers/helpers';
import { jam } from '@jam.dev/sdk';
import { useQuery } from '@tanstack/react-query';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { logger } from 'config/logger';
import { QueryKey } from 'types/query';

export enum BrowserExtensionInfoStatus {
  Unknown = 'Unknown',
  Active = 'Active',
  NotInstalled = 'Not Installed',
  RuntimeNotAvailable = 'Runtime Not Available',
  Error = 'Error',
}

type BrowserExtensionInfo = {
  manifest?: object;
  version?: string;
  status?: BrowserExtensionInfoStatus | string;
};

const notInstalled: BrowserExtensionInfo = { status: BrowserExtensionInfoStatus.NotInstalled };

export const useBrowserExtensionStatusQuery = () => {
  return useQuery(
    [QueryKey.useBrowserExtensionStatusQuery],
    async () => {
      const [response, error] = await getExtensionStatusV2();
      const extensionInfo = {
        status: response?.status,
        version: response?.version,
        error: trySerialize(error),
      };

      jam.metadata(() => ({ extensionInfo }));
      console.log('extensionInfo', extensionInfo);

      return response;
    },
    { initialData: notInstalled, initialDataUpdatedAt: 0 },
  );
};

const getExtensionStatusV2 = async (): Promise<[BrowserExtensionInfo, unknown?]> => {
  if (!chrome.runtime) {
    return [{ status: BrowserExtensionInfoStatus.RuntimeNotAvailable }];
  }

  try {
    const response = await extensionClient.sendMessage('getExtensionStatus');

    if (isObject(response) && response?.status === BrowserExtensionInfoStatus.Active) {
      return [response];
    }

    return [{ status: BrowserExtensionInfoStatus.Unknown }, response];
  } catch (e) {
    logger.error(e);

    return [{ status: BrowserExtensionInfoStatus.Error }, e];
  }
};

const trySerialize = (obj: unknown): string => {
  try {
    if (obj instanceof Error) {
      return JSON.stringify({ message: obj.message, stack: obj.stack, name: obj.name });
    }

    return JSON.stringify(obj);
  } catch (e) {
    return 'not serializable';
  }
};

const extensionClient = {
  sendMessage: async (key: string, data?: any) => {
    const extensionId = process.env.REACT_APP_CHROME_EXTENSION_ID || '';

    return chrome.runtime.sendMessage(extensionId, { key, data });
  },
};

export const getExtensionStatus = async (): Promise<BrowserExtensionInfo> => {
  try {
    const response = await browserExtensionClient.sendMessage<Partial<BrowserExtensionInfo>>('getExtensionStatus');

    if (Object.keys(response).length === 0) {
      return { status: BrowserExtensionInfoStatus.Unknown };
    }

    return response;
  } catch (e) {
    logger.error(e);

    return { status: BrowserExtensionInfoStatus.Error };
  }
};
